import { useContext, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { HeaderSlotContext } from './headerSlot';

interface PageHeaderProps {
  title: ReactNode;
  /** One line under the title. Hidden on phones, where the bar has no room. */
  subtitle?: ReactNode;
  /**
   * A back arrow before the title, for detail and form pages: a path to link
   * to, or a handler (e.g. `() => navigate(-1)`).
   */
  back?: string | (() => void);
  /** Accessible name for the back arrow. */
  backLabel?: string;
}

/**
 * The page's title and one-line description, drawn in the top bar instead of
 * at the top of the page.
 *
 * Only the words move. Page actions (filters, period pickers, buttons) stay in
 * the page, where there is room for them at every width.
 *
 * Renders nothing in place (a portal), so it can sit first in any `space-y-*`
 * stack without leaving a gap behind.
 */
const PageHeader = ({ title, subtitle, back, backLabel = 'Back' }: PageHeaderProps) => {
  const slot = useContext(HeaderSlotContext);

  const backClass =
    'inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-500 transition-colors hover:bg-slate-50 hover:text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-300';
  const backButton =
    back === undefined ? null : typeof back === 'string' ? (
      <Link to={back} aria-label={backLabel} title={backLabel} className={backClass}>
        <ArrowLeft size={18} />
      </Link>
    ) : (
      <button type="button" onClick={back} aria-label={backLabel} title={backLabel} className={backClass}>
        <ArrowLeft size={18} />
      </button>
    );

  // Outside the shell there is no bar: draw it where the page put it.
  if (slot === undefined) {
    return (
      <div className="mb-6 flex items-start gap-3">
        {backButton}
        <div className="min-w-0">
          <h1 className="text-xl font-semibold text-slate-900">{title}</h1>
          {subtitle && <p className="mt-1 text-sm text-slate-500">{subtitle}</p>}
        </div>
      </div>
    );
  }

  // The bar mounts in the same pass as the page; the title follows a frame later.
  if (!slot) return null;

  return createPortal(
    <div className="flex min-w-0 items-center gap-3">
      {backButton}
      <div className="min-w-0">
        <h1 className="truncate text-lg font-semibold leading-tight text-slate-900 sm:text-xl">
          {title}
        </h1>
        {subtitle && (
          <p
            className="mt-0.5 hidden text-sm leading-snug text-slate-500 sm:line-clamp-1"
            title={typeof subtitle === 'string' ? subtitle : undefined}
          >
            {subtitle}
          </p>
        )}
      </div>
    </div>,
    slot,
  );
};

export default PageHeader;
